// ModalShell — EL modal único de la app (sistema de diseño AVANTE HOSPITAL-TECH).
//
// Regla de la casa (Erick, 4-jul-2026): PROHIBIDO el modal transparente/glass.
// Todo diálogo de la app usa este shell para que nunca vuelva a salir una
// ventana que se confunda con el contenido de atrás:
//   - backdrop a pantalla completa rgba(10,10,25,.72) + blur(4px)
//   - panel SÓLIDO linear-gradient(160deg,#20204a,#161634) (cero alpha)
//   - borde rgba(140,170,230,.22), radio 24px, sombra 0 40px 90px -20px
//   - header Space Grotesk 600 + subtítulo #94A0CD + divisor + ✕ de 44px
//   - centrado, max-width 560-640, scroll interno
// Solo capa visual: el contenido y sus handlers los pone cada modal.
import { type CSSProperties, type ReactNode } from 'react'

export interface ModalShellProps {
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  /** deshabilita cierre por backdrop/✕ mientras se envía un formulario */
  closeDisabled?: boolean
  /** 560 (default) a 640 para formularios anchos */
  maxWidth?: number
  ariaLabel?: string
  children: ReactNode
  /** área fija bajo el contenido (botonera); va dentro del panel */
  footer?: ReactNode
  /** z-index del overlay (default 50; usar 60+ para modal sobre modal) */
  zIndex?: number
}

export function ModalShell({
  title,
  subtitle,
  onClose,
  closeDisabled = false,
  maxWidth = 560,
  ariaLabel,
  children,
  footer,
  zIndex = 50,
}: ModalShellProps) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        zIndex,
        background: 'rgba(10,10,25,.72)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={() => !closeDisabled && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        className="av-modal w-full flex flex-col"
        style={{
          background: 'linear-gradient(160deg,#20204a,#161634)',
          border: '1px solid rgba(140,170,230,.22)',
          borderRadius: 24,
          boxShadow: '0 40px 90px -20px rgba(0,0,0,.7)',
          maxWidth,
          maxHeight: '90vh',
          padding: '26px 28px',
          color: '#EAF2FF',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2
              className="text-lg truncate"
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}
            >
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs mt-1" style={{ color: '#94A0CD' }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Cerrar"
            className="flex items-center justify-center rounded-xl shrink-0 disabled:opacity-40"
            style={{
              width: 44,
              height: 44,
              color: '#94A0CD',
              border: '1px solid rgba(140,170,230,.15)',
            }}
          >
            ✕
          </button>
        </div>

        <div
          className="my-4 shrink-0"
          style={{ borderTop: '1px solid rgba(140,170,230,.15)' }}
        />

        <div className="overflow-y-auto flex-1 min-h-0">{children}</div>

        {footer && (
          <div
            className="pt-4 mt-4 shrink-0"
            style={{ borderTop: '1px solid rgba(140,170,230,.15)' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------
// Estilos compartidos para el CONTENIDO de los modales (inputs, botones,
// sub-tarjetas, tablas). Usarlos evita texto gris-oscuro ilegible sobre
// el panel navy.
// ------------------------------------------------------------------
export const m: Record<string, CSSProperties> = {
  label: { color: '#EAF2FF' },
  labelSuave: { color: '#94A0CD' },
  input: {
    background: 'rgba(255,255,255,.06)',
    border: '1px solid rgba(140,170,230,.22)',
    color: '#EAF2FF',
    borderRadius: 10,
  },
  subCard: {
    background: 'rgba(255,255,255,.04)',
    border: '1px solid rgba(140,170,230,.15)',
    borderRadius: 14,
  },
  btnPrimary: { background: '#006EB4', color: '#fff' },
  btnDanger: { background: '#FF5C7A', color: '#fff' },
  btnGhost: {
    background: 'transparent',
    color: '#EAF2FF',
    border: '1px solid rgba(140,170,230,.22)',
  },
  textDanger: { color: '#FF5C7A' },
  textWarn: { color: '#F5A623' },
  textOk: { color: '#2FE08A' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  monoCyan: { fontFamily: "'JetBrains Mono', monospace", color: '#7FD4FF' },
  // tablas internas (regla 6): cabecera 11px mayúsculas, zebra sutil
  th: {
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '.6px',
    color: '#7FD4FF',
    fontFamily: "'Space Grotesk', sans-serif",
    borderBottom: '1px solid rgba(140,170,230,.18)',
  },
  trZebra: { background: 'rgba(255,255,255,.03)' },
  tdBorde: { borderBottom: '1px solid rgba(140,170,230,.08)' },
}

/** Píldora de estado (ej. DISPONIBLE cyan; variantes warn/danger/ok). */
export function Pill({
  children,
  tone = 'cyan',
}: {
  children: ReactNode
  tone?: 'cyan' | 'warn' | 'danger' | 'ok' | 'neutral'
}) {
  const tones: Record<string, CSSProperties> = {
    cyan: { background: 'rgba(0,110,180,.35)', color: '#7FD4FF', border: '1px solid rgba(127,212,255,.35)' },
    warn: { background: 'rgba(245,166,35,.15)', color: '#F5A623', border: '1px solid rgba(245,166,35,.4)' },
    danger: { background: 'rgba(255,92,122,.15)', color: '#FF5C7A', border: '1px solid rgba(255,92,122,.4)' },
    ok: { background: 'rgba(47,224,138,.12)', color: '#2FE08A', border: '1px solid rgba(47,224,138,.35)' },
    neutral: { background: 'rgba(255,255,255,.06)', color: '#94A0CD', border: '1px solid rgba(140,170,230,.2)' },
  }
  return (
    <span
      className="inline-block text-xs px-2 py-0.5 rounded-full font-semibold"
      style={tones[tone]}
    >
      {children}
    </span>
  )
}
